import { format, isValid, parseISO } from 'date-fns';

// ── Conversations (/conversations) ───────────────────────────────────

export type ConversationLine = { role: 'agent' | 'user'; text: string };

export type ConversationSession = {
  id: string;
  name: string;
  phone: string;
  agent: string;
  agentIcon: string;
  duration: string;
  outcome: string;
  // null means no sentiment score exists (auto_tag_sentiment disabled, or not
  // yet computed) — distinct from a real, computed neutral score of 0.
  sentiment: number | null;
  time: string;
  title: string;
  campaign: string;
  campaignId: string | null;
  transcript: ConversationLine[];
  turns_count: number | null;
  cost_cents: number | null;
};

/** Turn a single-line or multi-line "Agent: … / User: …" blob into transcript lines. */
export function parseTranscriptStringBlob(blob: string): ConversationLine[] {
  const out: ConversationLine[] = [];
  const chunks = blob.trim().split(/\n+/);
  for (const chunk of chunks) {
    const line = chunk.trim();
    if (!line) continue;
    const m = /^(Agent|User|Caller|Customer|Assistant):\s*(.*)$/i.exec(line);
    if (m) {
      const who = m[1].toLowerCase();
      const role: 'agent' | 'user' =
        who === 'user' || who === 'caller' || who === 'customer' ? 'user' : 'agent';
      const text = m[2]?.trim() ?? '';
      if (text) out.push({ role, text });
    } else {
      out.push({ role: 'agent', text: line });
    }
  }
  return out;
}

/** Format session API ISO timestamps for the conversations list (viewer-local timezone). */
function formatVoiceSessionDisplayTime(raw: unknown): string {
  if (raw == null) return '';
  const str = String(raw).trim();
  if (!str) return '';
  const parsed = parseISO(str);
  const dt = isValid(parsed) ? parsed : new Date(str);
  if (!isValid(dt)) return str;
  return format(dt, 'MMM d, yyyy · h:mm a');
}

/** Map one voice session record from `/api/voice/sessions` into the conversations UI model. */
export function mapVoiceSessionToConversation(s: any): ConversationSession {
  const transcript: ConversationLine[] = [];
  const turnsFirst = s.turns ?? s.messages;
  if (Array.isArray(turnsFirst) && turnsFirst.length > 0) {
    for (const line of turnsFirst) {
      const roleRaw = (line.role ?? line.speaker ?? line.from ?? 'agent') as string;
      const role: 'agent' | 'user' = roleRaw === 'user' || roleRaw === 'caller' || roleRaw === 'human' ? 'user' : 'agent';
      const text = String(line.content ?? line.text ?? line.message ?? '');
      if (text) transcript.push({ role, text });
    }
  } else if (typeof s.transcript === 'string' && s.transcript.trim()) {
    transcript.push(...parseTranscriptStringBlob(s.transcript));
  }
  return {
    id: String(s.id ?? s.session_id ?? ''),
    name: String(s.caller_name ?? s.contact_name ?? s.name ?? ''),
    phone: String(s.caller_phone ?? s.phone ?? s.phone_number ?? ''),
    agent: String(s.agent?.name ?? s.agent_name ?? s.agent_id ?? ''),
    agentIcon: String(s.agent_icon ?? s.agent?.icon ?? ''),
    duration: String(
      s.duration ??
        s.duration_label ??
        s.duration_display ??
        (s.duration_seconds != null && !Number.isNaN(Number(s.duration_seconds))
          ? String(Math.max(0, Math.floor(Number(s.duration_seconds))))
          : ''),
    ),
    outcome: String(s.outcome ?? s.result ?? s.status ?? ''),
    sentiment: (s.sentiment ?? s.sentiment_score) != null ? Number(s.sentiment ?? s.sentiment_score) : null,
    time: formatVoiceSessionDisplayTime(
      [s.time, s.display_time, s.started_at, s.created_at, s.ended_at].find((v) => v != null && String(v).trim() !== '') ??
        '',
    ),
    title: String(
      s.title ||
        s.caller_name ||
        s.contact_name ||
        s.name ||
        s.caller_phone ||
        s.phone ||
        s.phone_number ||
        s.summary?.topic ||
        s.topic ||
        s.outcome ||
        s.result ||
        s.channel ||
        s.direction ||
        'Conversation'
    ),
    campaign: String(s.campaign?.name ?? s.campaign_name ?? s.campaign_title ?? s.campaign_id ?? ''),
    campaignId: s.campaign_id ?? s.campaign?.id ?? null,
    transcript,
    turns_count: s.turns_count != null ? Number(s.turns_count) : (Array.isArray(s.turns) ? s.turns.length : null),
    cost_cents: s.cost_cents != null ? Number(s.cost_cents) : null,
  };
}
